import { createActionCreator } from '@pressreader/appstore';
import {
    AnySignInWithSystem,
    AnySignUpAsSystemData,
    AuthFailure,
    AuthSystem,
    IAuthenticationAnalyticsContext,
    IAuthError,
    IUser,
    IUserSettings,
    LibraryExternalAuthProvider,
    LibraryPostAuthOptions,
    SocialExternalAuthProvider,
} from '@pressreader/authentication-types';

import { AUTHENTICATION_CORE_MODULE_NAME } from '../../constants';

const prefix = `${AUTHENTICATION_CORE_MODULE_NAME}/common/`;

export enum UserChangeReason {
    Unknown,

    Login,
    Logout,
    SignUp,
}

interface IInitializedPayload {
    user: IUser;
    userSettings: IUserSettings;
}

interface ILoginPayload {
    loginAs: AnySignInWithSystem;
    analyticsCtx?: IAuthenticationAnalyticsContext;
}

interface ILoginBeganPayload {
    analyticsCtx?: IAuthenticationAnalyticsContext;
}

interface ILoginDonePayload {
    system: AuthSystem;

    user: IUser;
    userSettings: IUserSettings;

    provider?: SocialExternalAuthProvider | LibraryExternalAuthProvider;

    analyticsCtx?: IAuthenticationAnalyticsContext;

    authOptions?: LibraryPostAuthOptions;

    libraryId?: number;
    /** The external provider's sign-in created the account. */
    isNewUser?: boolean;
}

interface ILoginFailedPayload {
    system: AuthSystem;

    error: IAuthError;

    provider?: string;
    libraryId?: number;
    failure?: AuthFailure;
    analyticsCtx?: IAuthenticationAnalyticsContext;
}

interface ILogOutFailedPayload {
    error: IAuthError;
}

interface ISignUpPayload {
    signUpAs: AnySignUpAsSystemData;
    analyticsCtx?: IAuthenticationAnalyticsContext;
}

interface ISignUpBeganPayload {
    analyticsCtx?: IAuthenticationAnalyticsContext;
}

interface ISignUpDonePayload {
    /** Registered user. */
    user: IUser;
    userSettings: IUserSettings;

    emailValidationRequired: boolean;
    email?: string;

    analyticsCtx?: IAuthenticationAnalyticsContext;

    /** `library` after a library card sign-in, else `email`. */
    system?: AuthSystem;
    provider?: string;
    libraryId?: number;
}

interface ISignUpFailedPayload {
    error: IAuthError;

    system?: AuthSystem;
    provider?: string;
    libraryId?: number;
    failure?: AuthFailure;
    analyticsCtx?: IAuthenticationAnalyticsContext;
}

interface IUserChangeBeganPayload {
    reason: UserChangeReason;
}

interface IUserChangeDonePayload {
    reason: UserChangeReason;
}

interface IUserChangeFailedPayload {
    reason: UserChangeReason;
    error: IAuthError;
}

interface IRecoverPwdPayload {
    email: string;
}

interface IPwdRecoveryBeganPayload {
    email: string;
}

interface IPwdRecoveryDonePayload {
    email: string;
}

interface IPwdRecoveryFailedPayload {
    email: string;
    error: string;
}

interface IUserAvatarChangedPayload {
    imageId: string;
}

export interface IdentityTokenChangedPayload {
    /** Normalized identity key the auth token carried before this change. */
    previousKey: string;
    /** Normalized identity key the auth token carries now. */
    currentKey: string;
    /** Monotonic identity generation (`svcAuth.identityGeneration()`) the new key was published at. */
    generation: number;
}

export const authInitialized = createActionCreator<IInitializedPayload>(`${prefix}initialized`);

export const loginAction = createActionCreator<ILoginPayload>(`${prefix}login`);
export const loginBegan = createActionCreator<ILoginBeganPayload>(`${prefix}login_began`);
export const loginDone = createActionCreator<ILoginDonePayload>(`${prefix}login_done`);
export const loginFailed = createActionCreator<ILoginFailedPayload>(`${prefix}login_failed`);

export const logOutRequestAction = createActionCreator(`${prefix}logout_request`);
export const logOutBegan = createActionCreator(`${prefix}logout_began`);
export const logOutDone = createActionCreator(`${prefix}logout_done`);
export const logOutFailed = createActionCreator<ILogOutFailedPayload>(`${prefix}logout_failed`);

export const signUpAction = createActionCreator<ISignUpPayload>(`${prefix}signup`);
export const signUpBegan = createActionCreator<ISignUpBeganPayload>(`${prefix}signup_began`);
export const signUpDone = createActionCreator<ISignUpDonePayload>(`${prefix}signup_done`);
export const signUpFailed = createActionCreator<ISignUpFailedPayload>(`${prefix}signup_failed`);

export const resetAuthStateAction = createActionCreator(`${prefix}reset_auth_state`);

export const clearLibraryPostAuthOptionsAction = createActionCreator(`${prefix}clear_library_post_auth_options`);

export const userChangeBegan = createActionCreator<IUserChangeBeganPayload>(`${prefix}user_change_began`);
export const userChangeDone = createActionCreator<IUserChangeDonePayload>(`${prefix}user_change_done`);
export const userChangeFailed = createActionCreator<IUserChangeFailedPayload>(`${prefix}user_change_failed`);

export const recoverPwdAction = createActionCreator<IRecoverPwdPayload>(`${prefix}recover_pwd`);
export const pwdRecoveryBegan = createActionCreator<IPwdRecoveryBeganPayload>(`${prefix}pwd_recovery_began`);
export const pwdRecoveryDone = createActionCreator<IPwdRecoveryDonePayload>(`${prefix}pwd_recovery_done`);
export const pwdRecoveryFailed = createActionCreator<IPwdRecoveryFailedPayload>(`${prefix}pwd_recovery_failed`);

export const userAvatarChanged = createActionCreator<IUserAvatarChangedPayload>(`${prefix}user_avatar_changed`);

/**
 * The auth TOKEN switched identity — the earliest, most complete signal there is: it covers the Redux-visible
 * transitions (login / sign-up / logout / account switch) AND the ones no auth action accompanies (legacy
 * `authorizeUser`-only sign-ins, cross-tab logout, a silent `/refresh` that comes back as somebody else).
 * Published by the always-running auth-core saga bridge, so it never depends on which feature loaded first.
 *
 * NOT a commit: the auth slice still holds the previous user when this fires, and on the commit paths cached
 * data is reconciled at commit time. Consumers that must react instantly (e.g. hiding a user-scoped surface)
 * key on this action; consumers that need the new user key on `userChangeDone` as before.
 *
 * A bridge-only transition produces no commit at all: auth-core's mismatch recheck asks for the RTK Query
 * caches to be reconciled under the token identity instead (see `identityReconcileRequested`, in
 * ./identity.internal). The auth slice
 * staying stale on those transitions is a known, out-of-scope gap, separable from the cache repair.
 */
export const identityTokenChanged = createActionCreator<IdentityTokenChangedPayload>(`${prefix}identity_token_changed`);
